import InteractableCylinder from "./builder/interactable-cylinder";
import InteractableCone from "./builder/interactable-cone";
import InteractableSphere from "./builder/interactable-sphere";
import InteractableTriangle from "./builder/interactable-triangle";
import InteractableCuboid from "./builder/interactable-cuboid";
import RenderManager from "../rendering/render-manager";
import SlideManager from "../utils/slide-manager";
import Input from "../interactivity/inputs";
import { fire } from "../utils/events";

const raycaster = new THREE.Raycaster();
const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0));

class InteractableGeometry extends THREE.Group {
    constructor(type) {

        super();

        this.type = "InteractableGeometry";
        this.isInteractableGeometry = true;
        this.geometryType = type;

        switch (type) {
            case "cylinder": this._interactables = new InteractableCylinder(); break;
            case "cone": this._interactables = new InteractableCone(); break;
            case "sphere": this._interactables = new InteractableSphere(); break;
            case "triangle-right-angle": this._interactables = new InteractableTriangle(); break;
            case "cuboid": this._interactables = new InteractableCuboid(); break;
        }

        this._state = {
            currentSelection: null,
            originalValue: 0,
            originalPoint: new THREE.Vector3(),
            isRotating: false
        };

        this.add(...this._interactables.objects);

        this.geometryType = type;
        this._interactables.objects.forEach(o => o.dontShowInHierarchyPanel = true);

        let visible = this.visible;
        Object.defineProperty(this, "visible", {
            get: () => visible,
            set: (enabled) => {
                visible = enabled;
                RenderManager.shadowMap.needsUpdate = true;
            },
        });
    }
}

const material = new THREE.MeshPhongMaterial({ color: 0xFFE4E1, side: THREE.DoubleSide, transparent: true });
const geometryRect = new THREE.PlaneBufferGeometry(1, 1);
const geometryCircle = new THREE.CircleBufferGeometry(1, 64);
const areaRect = new THREE.Mesh(geometryRect, material);
const areaCircle1 = new THREE.Mesh(geometryCircle, material);
const areaCircle2 = new THREE.Mesh(geometryCircle, material);

geometryCircle.rotateX(Math.PI * 0.5);
areaRect.rotateX(Math.PI * 0.5);

{
    const lineMat = new THREE.MeshPhongMaterial({ color: 0x000000, side: THREE.DoubleSide, transparent: true });
    const ringGeo = new THREE.RingBufferGeometry(1, 1.1, 64);
    const line1 = new THREE.Mesh(ringGeo, lineMat);
    const line2 = new THREE.Mesh(ringGeo, lineMat);

    ringGeo.rotateX(Math.PI * 0.5);

    areaCircle1.add(line1);
    areaCircle2.add(line2);
}

{
    const lineMat = new THREE.MeshPhongMaterial({ color: 0x000000, side: THREE.DoubleSide, transparent: true });
    const tmpGeo = new THREE.PlaneBufferGeometry(1, 1);

    const lineGeo = new THREE.BufferGeometry();
    const arrPositions = [], arrNormals = [], arrUvs = [], arrIndices = [];
    const sizes = [[1.1, 0.05, 1], [1.1, 0.05, 1], [0.05, 1, 1], [0.05, 1, 1]];
    const offsets = [[0, 0.525, 0], [0, -0.525, 0], [0.525, 0, 0], [-0.525, 0, 0]];

    for (let i = 0; i < 4; i++) {
        tmpGeo.index.array.forEach(v => arrIndices.push(v + i * 4));
        arrNormals.push(...tmpGeo.attributes.normal.array);
        arrUvs.push(...tmpGeo.attributes.uv.array);
        tmpGeo.attributes.position.array.forEach((v, j) => {
            const ax = j % 3;
            const size = sizes[i][ax];
            const offset = offsets[i][ax];

            arrPositions.push(v * size + offset);
        });
    }

    lineGeo.addAttribute("position", new THREE.Float32BufferAttribute(arrPositions, 3));
    lineGeo.addAttribute("uv", new THREE.Float32BufferAttribute(arrUvs, 2));
    lineGeo.addAttribute("normal", new THREE.Float32BufferAttribute(arrNormals, 3));
    lineGeo.setIndex(new THREE.Uint8Attribute(arrIndices, 1));

    const line1 = new THREE.Mesh(lineGeo, lineMat);

    areaRect.add(line1);
}

areaRect.position.set(1 + 1 * 1, 0.5, 0);
areaCircle1.position.set(1 * 2 + 2 * 1, 0.5, 1.5);
areaCircle2.position.set(1 * 2 + 2 * 1, 0.5, -1.5);
areaRect.scale.set(1, 2 * Math.PI * 1, 1);
areaCircle1.scale.set(1, 1, 1);
areaCircle2.scale.set(1, 1, 1);
areaCircle1.name = "areaCircle1";
areaCircle2.name = "areaCircle2";

let points, arrPoints;

const tryPickGeometry = (function () {

    const state = {
        currentSelection: null,
        currentInteractable: null,
        originalValue: 0,
        originalPoint: new THREE.Vector3(),
        isRotating: false
    };

    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const line = new THREE.Line3(a, b);
    const tmp = new THREE.Vector3();

    function setLabelVisibility(visible) {
        const display = visible ? 'block' : 'none';

        for (let { dom } of arrPoints)
            dom.style.display = display;
    }

    function updateValues() {
        const camera = RenderManager.activeCamera;
        const { width, height } = RenderManager.getSize();

        arrPoints.forEach(p => {
            const parent = p.parent || RenderManager.activeScene;
            tmp.copy(p.point).applyMatrix4(parent.matrixWorld).project(camera);

            const px = Math.round(((width * tmp.x) + width) * 0.5);
            const py = Math.round((height - (height * tmp.y)) * 0.5);

            p.dom.style.left = px - p.dom.clientWidth * 0.5 + "px";
            p.dom.style.top = py - p.dom.clientHeight * 0.5 + "px";
        });
    }


    function findInteractable({ interactables, mouse, camera }) {
        const intersections = [];

        raycaster.setFromCamera(mouse.position, camera);

        for (let interactable of interactables) {
            const colliders = Object.keys(interactable._interactables.colliders).reduce((accum, key) => {
                const collider = interactable._interactables.colliders[key];

                accum.push(collider);

                collider.deselect();

                return accum;
            }, []);

            for (let collider of colliders) {
                const _intersections = [];
                collider.raycast(raycaster, _intersections);

                if (_intersections.length > 0) {
                    _intersections[0].interactable = interactable;
                    intersections.push(_intersections[0]);
                    break;
                }
            }
        }

        intersections.sort((a, b) => a.distance - b.distance);

        return intersections[0] || null;
    }

    return function ({ interactables, mouse, camera, controls }) {
        setLabelVisibility(interactables.length !== 0);

        if (interactables.length === 0) return null;

        updateValues();

        let intersection = state.currentSelection;

        raycaster.setFromCamera(mouse.position, camera);

        if (state.isRotating && !mouse.states[1]) state.isRotating = false;

        if (!state.isRotating) {
            if (!state.currentSelection) { intersection = findInteractable({ interactables, mouse, camera }); }

            if (intersection) { intersection.object.highlight(); }

            if (!state.currentSelection && !intersection && !state.isRotating && mouse.states[1]) {
                state.isRotating = true;
                return { object: null, interactable: null };
            }

            a.copy(raycaster.ray.origin);
            b.copy(raycaster.ray.direction).multiplyScalar(camera.far).add(raycaster.ray.origin);

            if (mouse.states[1]) {
                if (intersection && !state.currentSelection) {
                    state.currentSelection = intersection;
                    intersection.object.highlight();

                    plane.set(raycaster.ray.direction.clone().negate(), 0).applyMatrix4(intersection.interactable.matrixWorld);

                    state.originalValue = intersection.interactable._interactables[intersection.object.interactableType];
                    plane.intersectLine(line, state.originalPoint);

                    controls.enabled = false;
                }
            } else {
                state.currentSelection = null;

                if (!SlideManager.currentSlide.camera.disabled) controls.enabled = true;

                fire('interactable_geometry_changed');
            }

            if (state.currentSelection && state.currentSelection.interactable === intersection.interactable) {
                const point = plane.intersectLine(line, tmp);
                const diff = point.distanceTo(intersection.interactable.position);
                const dt = state.originalPoint.distanceTo(intersection.interactable.position);

                intersection.interactable._interactables[state.currentSelection.object.interactableType] = diff - (dt - state.originalValue);

                RenderManager.shadowMap.needsUpdate = true;
            }
        }

        return { object: state.currentSelection && state.currentSelection.interactable, interactable: state.currentSelection && state.currentSelection.object };
    };
})();

const interactableGeometries = new Set();

let group;

const updateInteractableGeometries = (function () {
    let NAMESPACE_SVG, svgGroup;
    let lastObject = null, lastIntersectable = null, hoveringFormula = null;
    let isInit = false;


    function init() {
        NAMESPACE_SVG = "http://www.w3.org/2000/svg"; //DevSkim: ignore DS137138
        svgGroup = document.createElementNS(NAMESPACE_SVG, "g");

        isInit = true;
    }

    return function () {
        if (!isInit) init();

        if (interactableGeometries.size === 0) return;
        if (!group.visible) {
            const formulas = [];
            const values = [];

            svgGroup.innerHTML = "";
            fire('cylinder_change_values', { object: group, formulas, values, hidden: true });

            Object
                .values(points)
                .forEach(({ dom }) => dom.style.display = "none");

            return;
        }

        Object
                .values(points)
                .forEach(({ dom }) => dom.style.display = "block");

        let visibleInteractables = [];

        for (let interactable of interactableGeometries) {
            if (interactable.parent) {
                if (interactable.visible) visibleInteractables.push(interactable);
            } else interactableGeometries.delete(interactable);
        }

        // group.visible = visibleInteractables.length !== 0;

        const mouse = Input.mouse;
        const { object, interactable } = tryPickGeometry({
            interactables: visibleInteractables,
            mouse: { position: mouse.position, states: { 1: mouse.which.left } },
            controls: Q3.controls,
            camera: RenderManager.activeCamera
        }) || { object: null, interactable: null };

        if (lastObject !== object && lastIntersectable !== object && object !== null)
            hoveringFormula = null;

        lastObject = object || lastObject;
        lastIntersectable = interactable || lastIntersectable;

        // view3d.addEventListener("mousemove", () => hoveringFormula = null, false);

        if (lastObject && lastIntersectable) {
            svgGroup.innerHTML = "";

            const r = lastObject._interactables.radius;
            const h = lastObject._interactables.height;

            let formulas = lastObject._interactables.formulas;
            let values = [];
            for (let { name } of formulas) {
                values.push(lastObject._interactables[name]);
            }

            fire('cylinder_change_values', { object: lastObject.overridePicker, formulas, values, hidden: false });

            const rect = document.createElementNS(NAMESPACE_SVG, "rect");
            svgGroup.appendChild(rect);
            rect.setAttribute("width", Math.abs(h * 10));
            rect.setAttribute("height", Math.abs(r * 10 * 2));
            rect.setAttribute("fill", "#cccccc");
            rect.setAttribute("stroke", "black");

            const circle = document.createElementNS(NAMESPACE_SVG, "circle");
            svgGroup.appendChild(circle);
            circle.setAttribute("r", Math.abs(r * 10));
            circle.setAttribute("fill", "#cccccc");
            circle.setAttribute("stroke", "black");
            circle.setAttribute("cy", r * 10 + r * 10 * 2);
            circle.setAttribute("cx", r * 10);

            areaRect.scale.set(h, 2 * Math.PI * r, 1);
            areaCircle1.scale.set(r, r, r);
            areaCircle2.scale.set(r, r, r);

            areaRect.position.set(r + h * 1, 0.5, 0);
            areaCircle1.position.set(r * 2 + h * 2, 0.5, r * 1.5);
            areaCircle2.position.set(r * 2 + h * 2, 0.5, r * -1.5);
            // areaCircle1.position.set(r * 2.5 + h * 1 + 1, 0.5, r * 1.5);
            // areaCircle2.position.set(r * 2.5 + h * 1 + 1, 0.5, r * -1.5);

            if (points.htmlAreaCircle1) points.htmlAreaCircle1.dom.innerHTML = points.htmlAreaCircle2.dom.innerHTML = (Math.PI * 2 * (r * r)).toFixed(2) + "cm²";
            if (points.htmlAreaRect) points.htmlAreaRect.dom.innerHTML = (Math.PI * 2 * (r * r) + 2 * Math.PI * h).toFixed(2) + "cm²";
            if (points.htmlHeight) points.htmlHeight.dom.innerHTML = (h).toFixed(2) + "cm";
            if (points.htmlRadius) points.htmlRadius.dom.innerHTML = (r).toFixed(2) + "cm";

            // if(points.htmlAreaCircle1) points.htmlAreaCircle1.point.copy(areaCircle1.position);
            // if(points.htmlAreaCircle2) points.htmlAreaCircle2.point.copy(areaCircle2.position);
            // if(points.htmlAreaRect) points.htmlAreaRect.point.copy(points.htmlAreaRect);
            if (points.htmlRadius) points.htmlRadius.point.set(0, h, 0);

            RenderManager.activeCamera.update = true;
        }
    }
})();

const addTeachingCylinder = (function () {
    const type = "cylinder";

    group = new THREE.Object3D();
    // group.dontShowInHierarchyPanel = true;
    let table = null;

    let created = false;

    return function () {
        created = true;
        const overrides = [areaCircle1, areaCircle2, areaRect];

        if (!table) {
            const tableGeo = new THREE.BoxBufferGeometry(100, 1, 100);
            tableGeo.translate(0, -0.5, 0);
            // const tableMat = MaterialBuilder.build({ color: 0xF5F5DC, materialType: "pbr" });
            const tableMat = new THREE.MeshPhongMaterial({ color: 0xF5F5DC });

            table = new THREE.Mesh(tableGeo, tableMat);

            group.add(table);
            overrides.push(table);

            table.receiveShadow = true;

            const directional1 = new THREE.DirectionalLight(0xffffff, 1);

            directional1.position.set(10, 8, 10);
            directional1.castShadow = true;

            const ambient = new THREE.AmbientLight(0xffffff, 0.2);

            RenderManager.activeScene.add(directional1, ambient);

            // RenderManager.activeScene.add(group);

            group.add(areaCircle1, areaCircle2, areaRect);

        }

        if (!points) {
            points = {
                htmlHeight: { dom: document.createElement("div"), point: new THREE.Vector3(), parent: group },
                htmlRadius: { dom: document.createElement("div"), point: new THREE.Vector3(0, 1, 0), parent: group },
                htmlAreaRect: { dom: document.createElement("div"), point: areaRect.position, parent: group },
                htmlAreaCircle1: { dom: document.createElement("div"), point: areaCircle1.position, parent: group },
                htmlAreaCircle2: { dom: document.createElement("div"), point: areaCircle2.position, parent: group },
            };


            arrPoints = Object.keys(points).map(key => {
                RenderManager.domElement.appendChild(points[key].dom);

                points[key].dom.innerHTML = "1.00cm";

                points[key].dom.classList.add("projected-value");
                points[key].dom.classList.add("thumbs-ignore");

                return points[key];
            });

            if (points.htmlAreaCircle1) points.htmlAreaCircle1.dom.innerHTML += "²";
            if (points.htmlAreaCircle2) points.htmlAreaCircle2.dom.innerHTML += "²";
            if (points.htmlAreaRect) points.htmlAreaRect.dom.innerHTML += "²";
        }

        function addGeometry(type) {
            const interactableGeometry = new InteractableGeometry(type);

            interactableGeometry.castShadow = true;

            interactableGeometries.add(interactableGeometry);

            return interactableGeometry;
        }

        const interactable = addGeometry(type);

        group.add(interactable);
        RenderManager.activeCamera.update = true;

        overrides.push(interactable);
        overrides.forEach(o => {
            o.traverse(o => {
                o.overridePicker = group;

                if (!o.isMesh) return;
                RenderManager.raycaster.makeTrackable(o);
            });
        });

        let visible = group.visible;
        Object.defineProperty(group, "visible", {
            get: () => visible,
            set: (enabled) => {
                visible = enabled;
                updateInteractableGeometries();
            },
        });

        return group;
    }
})();

export default InteractableGeometry;
export { InteractableGeometry, tryPickGeometry, updateInteractableGeometries, addTeachingCylinder };